/**
 * Resizes an image to a maximum dimension while maintaining aspect ratio
 * @param source - Image source (HTMLImageElement or HTMLVideoElement)
 * @param maxSize - Maximum size for the longest dimension (default: 2048)
 * @param quality - JPEG quality (0-1, default: 0.8)
 * @returns Promise resolving to a resized Blob
 */
export async function resizeImage(
  source: HTMLImageElement | HTMLVideoElement,
  maxSize: number = 2048,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    // Get source dimensions
    const sourceWidth = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const sourceHeight = source instanceof HTMLVideoElement ? source.videoHeight : source.height;

    // Calculate resized dimensions (max size on longest side)
    let canvasWidth = sourceWidth;
    let canvasHeight = sourceHeight;

    if (sourceWidth > sourceHeight) {
      if (sourceWidth > maxSize) {
        canvasWidth = maxSize;
        canvasHeight = (sourceHeight * maxSize) / sourceWidth;
      }
    } else {
      if (sourceHeight > maxSize) {
        canvasHeight = maxSize;
        canvasWidth = (sourceWidth * maxSize) / sourceHeight;
      }
    }

    // Set canvas dimensions to resized size
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Draw the source with resized dimensions
    context.drawImage(source, 0, 0, sourceWidth, sourceHeight, 0, 0, canvasWidth, canvasHeight);

    // Convert to blob
    canvas.toBlob((blob) => {
      if (blob) {
        console.log(`Image resized from ${sourceWidth}x${sourceHeight} to ${canvasWidth}x${canvasHeight}, size: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
        resolve(blob);
      } else {
        reject(new Error('Failed to create blob from canvas'));
      }
    }, 'image/jpeg', quality);
  });
}

/**
 * Loads an image file and resizes it
 * @param file - The file to load and resize
 * @param maxSize - Maximum size for the longest dimension (default: 2048)
 * @param quality - JPEG quality (0-1, default: 0.8)
 * @returns Promise resolving to a resized File
 */
export async function resizeImageFile(
  file: File,
  maxSize: number = 2048,
  quality: number = 0.8
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = async () => {
      try {
        const blob = await resizeImage(img, maxSize, quality);
        const resizedFile = new File([blob], file.name, { type: 'image/jpeg' });
        URL.revokeObjectURL(img.src); // Clean up object URL
        resolve(resizedFile);
      } catch (error) {
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Flips an image (Blob or File) horizontally
 * @param source - The Blob or File to flip
 * @param quality - JPEG quality (0-1, default: 0.8)
 * @returns Promise resolving to a flipped Blob
 */
export async function flipImageHorizontally(
  source: Blob | File,
  quality: number = 0.8
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;
      
      // Flip horizontally
      ctx.save();
      ctx.scale(-1, 1);
      ctx.translate(-canvas.width, 0);
      ctx.drawImage(img, 0, 0);
      ctx.restore();
      
      // Convert to blob
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(img.src);
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      }, 'image/jpeg', quality);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(source);
  });
}

